import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import { Map } from 'immutable';
import { uniq } from 'lodash';
import PropTypes from 'prop-types';

import { showExitWithoutSavingModal } from 'actions/modals';

import {
  getCurrentLocationName,
  getLockTimesheetRolePermission,
  getRepackagingRollout,
  getTimesheetApprovalEnabled,
} from 'selectors/session';

import { toggleMessengerShown } from 'features/messengerv2/actions';
import * as timesheetsActions from 'features/timesheets/redux/actions/timesheets';
import { updateHasTimecards } from 'features/timesheets/redux/actions/timesheets';
import {
  getBreakPenaltiesEnabled,
  getLocationRolesUpdated,
} from 'features/timesheets/redux/selectors/timesheets';
import Navigation from 'features/timesheets/TimesheetsPage/TimecardModal/Navigation';
import {
  deleteTimecard,
  dismissMissedBreaks,
  fetchLocationData as fetchLocationDataSlice,
  fetchShiftData,
  fetchShiftOvertime,
  submitTimecardData,
  timecardModalActions,
  updateTimecardApproval,
} from 'features/timesheets/TimesheetsPage/TimecardModal/slices';
import { getShiftMetadata } from 'features/timesheets/TimesheetsPage/TimecardModal/utils';
import {
  EVENT_ACTIONS,
  TIMECARD_SELECT_INPUTS,
  trackDropDownInputSelected,
  trackRoleSelectInput,
  trackTimecardClick,
  trackTimecardModalClose,
} from 'features/timesheets/tracking';

import Loader from 'components/Loader';
import Modal from 'components/modals/Modal';

import { cxHelpers } from 'util/className';
import * as flashNotice from 'util/flashNotice';

import {
  getChangesMade,
  getCurrentShift,
  getEditableShift,
  getEditMode,
  getEmployeeOptions,
  getExpandBreaks,
  getFocusClockOut,
  getHasTimecard,
  getHideTipsSection,
  getIsLoading,
  getIsOpen,
  getIsSubmitting,
  getMandatedBreaks,
  getRoleOptions,
  getShifts,
  getStartingPath,
  getValidationErrors,
} from './selectors';
import { editTimecard } from './slices';
import TimecardModal from './TimecardModal';
import { DEFAULT_VALIDATION_ERRORS } from './utils';

const CUSTOM_STYLES = {
  overlay: { zIndex: 1000 },
};

const { cx } = cxHelpers('TimecardModal');

const findTimebreakIndex = (timebreaks, key) =>
  // new timebreaks do not have ID, but they have `key` attribute which is a random id
  timebreaks.findIndex(b =>
    !!b.get('id') ? b.get('id') === key : b.get('key') === key
  );

const TimecardView = ({
  addLocationRole,
  breakPenaltiesEnabled,
  closeModal,
  currentShift,
  // eslint-disable-next-line no-shadow
  dismissMissedBreaks,
  editMode,
  employeeOptions,
  expandBreaks,
  fetchData,
  fetchLocationData,
  // eslint-disable-next-line no-shadow
  fetchOvertime,
  focusClockOut,
  hasTimecard,
  hideTipsSection,
  isLoading,
  isOpen,
  isSubmitting,
  locationName,
  lockTimesheetsPermission,
  mandatedBreaks,
  onDelete,
  repackagingRolloutEnabled,
  roleOptions,
  setCurrentShiftId,
  setEditMode,
  setValidationErrors,
  shifts,
  startingPath,
  submitTimecardChanges,
  timesheetApprovalEnabled,
  onToggleMessengerShown,
  // eslint-disable-next-line no-shadow
  updateTimecardApproval,
  validationErrors,
  locationRolesUpdated,
  // eslint-disable-next-line no-shadow
  updateHasTimecards,
  onShowExitWithoutSavingModal,
  editableShift,
  setEditableShift,
  showTimecardChangesSavedToast,
  changesMade,
  setChangesMade,
}) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: '(max-width: 600px)' });
  const isLandscape = useMediaQuery({ maxHeight: 568, maxWidth: 812 });
  const [previousTrackingKey, setPreviousTrackingKey] = useState(null);
  const [modal, setModal] = useState('');

  const lockedMessage = currentShift.get('locked_message');
  const isEmbedded = useIsEmbedded();
  const handleChangeShift = useCallback(
    (field, value) => {
      trackRoleSelectInput(field, value);
      setEditableShift(shift => shift.merge({ [field]: value }));
    },
    [setEditableShift]
  );

  const trackBreakTypeSelect = useCallback(
    mb_id => {
      const timebreak = mandatedBreaks
        .filter(mb => mb.get('id') === mb_id)
        .toJS()[0];

      if (timebreak) {
        const trackingTimebreakType = timebreak.paid
          ? TIMECARD_SELECT_INPUTS.PAID_BREAK_TYPE
          : TIMECARD_SELECT_INPUTS.UNPAID_BREAK_TYPE;

        trackDropDownInputSelected({
          properties: { drop_down: trackingTimebreakType },
        });
      }
    },
    [mandatedBreaks]
  );

  const handleChangeTimebreak = useCallback(
    (key, field, value) => {
      if (field === 'mandated_break_id') {
        trackBreakTypeSelect(value);
      }
      setEditableShift(shift => {
        const index = findTimebreakIndex(shift.get('timebreaks'), key);

        return shift.mergeIn(['timebreaks', index], { [field]: value });
      });
    },
    [trackBreakTypeSelect, setEditableShift]
  );
  const handleChangeFormError = useCallback(
    (field, errorMessage) => {
      setEditableShift(shift => {
        let currentErrors = shift.get('form_errors') || Map();

        if (errorMessage) {
          currentErrors = currentErrors.set(field, errorMessage);
        } else {
          currentErrors = currentErrors.delete(field);
        }

        return shift.set('form_errors', currentErrors);
      });
    },
    [setEditableShift]
  );

  const handleAddTimebreak = useCallback(
    timebreak => {
      setEditableShift(shift =>
        shift.merge({
          timebreaks: shift.get('timebreaks').push(timebreak).toJS(),
        })
      );
    },
    [setEditableShift]
  );

  const handleUpdateLockedStatus = useCallback(async () => {
    const newIsLockedStatus = !Boolean(lockedMessage);
    trackTimecardClick({
      eventAction: newIsLockedStatus
        ? EVENT_ACTIONS.LOCK_CLICKED
        : EVENT_ACTIONS.UNLOCK_CLICKED,
    });

    await updateTimecardApproval({
      id: currentShift.get('id'),
      approve: newIsLockedStatus,
      originPath: window.location.pathname,
    });
  }, [currentShift, lockedMessage, updateTimecardApproval]);

  const handleRemoveTimebreak = useCallback(
    key => {
      setEditableShift(shift => {
        const index = findTimebreakIndex(shift.get('timebreaks'), key);

        return shift.setIn(['timebreaks', index, 'remove'], true);
      });
    },
    [setEditableShift]
  );

  const handleDismissMissedBreaks = useCallback(
    (key, waived) => {
      dismissMissedBreaks({
        id: currentShift.get('id'),
        mandated_break_id: key,
        waived,
        originPath: window.location.pathname,
      });
    },
    [currentShift, dismissMissedBreaks]
  );

  const enableEditMode = useCallback(() => {
    dispatch(editTimecard());
  }, [dispatch]);

  const disableEditMode = useCallback(() => {
    setValidationErrors(DEFAULT_VALIDATION_ERRORS);
    setEditMode(false);
  }, [setEditMode, setValidationErrors]);

  const canManageTimecards = useMemo(
    () => currentShift.get('can_manage_timecards') || false,
    [currentShift]
  );

  const currentShiftIssues = useMemo(() => {
    const issues = currentShift.get('issues')
      ? uniq(
          currentShift
            .get('issues')
            .filter(i => !i.get('resolved'))
            .map(i => i.get('type'))
            .toArray()
        )
      : [];
    return issues;
  }, [currentShift]);

  useEffect(() => {
    // For issues like `unscheduled_shift` where we merge a shift and generate a new shift ID,
    // we need to make sure we keep track of that and send over the original key,
    // rather than the new key to make sure the
    // events are properly created
    if (previousTrackingKey !== currentShift.get('id')) {
      setPreviousTrackingKey(currentShift.get('id'));
    }
  }, [currentShift, previousTrackingKey]);

  const submitChanges = useCallback(
    (jobId, data) => {
      data.timebreaks = data.timebreaks.filter(tb => !tb.remove && !tb.waived);
      // here's where the filtering of unwaived timebreaks happens
      submitTimecardChanges({ jobId, data }).then(({ error, payload }) => {
        if (error) {
          if (payload.general.length > 0) {
            flashNotice.error(payload.general);
          }
        } else {
          showTimecardChangesSavedToast();
          updateHasTimecards();
          // fetch overtime issue in a separate request
          fetchOvertime({ id: payload.id });
        }
      });
    },
    [
      submitTimecardChanges,
      showTimecardChangesSavedToast,
      updateHasTimecards,
      fetchOvertime,
    ]
  );

  const handleSave = useCallback(() => {
    submitChanges(editableShift.get('job_id'), {
      shift_id: editableShift.get('id'),
      ...editableShift.toJS(),
    });
    localStorage.setItem('scrollPosition', window.scrollY);
  }, [editableShift, submitChanges]);

  useEffect(() => {
    fetchLocationData();
  }, [fetchLocationData, locationRolesUpdated]);

  useEffect(() => {
    const id = currentShift.get('id');

    if (isOpen) {
      setModal('opened');
      if (id) {
        if (currentShift.get('loaded')) {
          return;
        } // do not fetch anything if the data is already loaded
        // fetch information from the server if it's an existing shift
        fetchData({ id }).then(() => {
          // fetch overtime issue for shift in separate request
          fetchOvertime({ id });
        });
      } else {
        // enable edit mode if it's a new shift
        enableEditMode();
      }
    }
  }, [
    currentShift,
    disableEditMode,
    enableEditMode,
    fetchData,
    fetchLocationData,
    fetchOvertime,
    isOpen,
    locationName,
  ]);

  useEffect(
    () => {
      setValidationErrors(DEFAULT_VALIDATION_ERRORS);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editableShift]
  );

  const handleCloseModal = useCallback(() => {
    setChangesMade(false);
    closeModal();
    onToggleMessengerShown(false);
    setModal('closed');
    trackTimecardModalClose(getShiftMetadata(editableShift));
  }, [closeModal, setChangesMade, onToggleMessengerShown, editableShift]);

  const handleOnClickClose = useCallback(() => {
    onShowExitWithoutSavingModal(handleCloseModal);
  }, [handleCloseModal, onShowExitWithoutSavingModal]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      auto
      closeOnRouteChange
      noPadding
      fullScreen={isEmbedded}
      fullWidth={isEmbedded}
      isOpen={isOpen}
      onRequestClose={changesMade ? handleOnClickClose : handleCloseModal}
      overflowVisible
      hideCloseIcon={isLoading}
      style={CUSTOM_STYLES}
      borderRadius={!isEmbedded && '8px'}
      newLayout
    >
      {isLoading && (
        <Loader
          isLoading={isLoading}
          className={cx()}
          h={isMobile || isEmbedded ? '100%' : 543}
          w={isMobile || isEmbedded ? '100%' : 640}
          centeredAbsolute
        />
      )}
      {!isLoading && (
        <TimecardModal
          addLocationRole={addLocationRole}
          addTimebreak={handleAddTimebreak}
          breakPenaltiesEnabled={breakPenaltiesEnabled}
          closeModal={handleCloseModal}
          currentShiftIssues={currentShiftIssues}
          deleteTimecard={onDelete}
          canManageTimecards={canManageTimecards}
          dismissMissedBreaks={handleDismissMissedBreaks}
          editMode={editMode}
          editableShift={editableShift}
          employeeOptions={employeeOptions}
          expandBreaks={expandBreaks}
          hasTimecard={hasTimecard}
          timesheetApprovalEnabled={timesheetApprovalEnabled}
          hasTimecardLockPermission={lockTimesheetsPermission}
          hideTipsSection={hideTipsSection}
          isLandscape={isLandscape}
          isLockDisabled={
            !lockTimesheetsPermission ||
            !timesheetApprovalEnabled ||
            !currentShift.get('actual_start_at')
          }
          isMobile={isMobile}
          isSubmitting={isSubmitting}
          mandatedBreaks={mandatedBreaks}
          modal={modal}
          onChangeShift={handleChangeShift}
          onChangeTimebreak={handleChangeTimebreak}
          onChangeFormError={handleChangeFormError}
          onDisableEditMode={disableEditMode}
          onSave={handleSave}
          onUpdateLockedStatus={handleUpdateLockedStatus}
          paywallVersion={
            repackagingRolloutEnabled
              ? 'timesheet_approval_repackaging_1'
              : null
          }
          previousTrackingKey={previousTrackingKey}
          removeTimebreak={handleRemoveTimebreak}
          roleOptions={roleOptions}
          shift={currentShift}
          startingPath={startingPath}
          submitChanges={submitChanges}
          validationErrors={validationErrors}
          changesMade={changesMade}
          setChangesMade={setChangesMade}
          focusClockOut={focusClockOut}
        />
      )}
      {!isLoading && currentShift.get('id') && (
        <Navigation
          currentShift={currentShift}
          isLoading={isLoading}
          setCurrentShiftId={setCurrentShiftId}
          shifts={shifts}
          isMobile={isMobile}
          changesMade={changesMade}
          setChangesMade={setChangesMade}
          setEditMode={setEditMode}
        />
      )}
    </Modal>
  );
};

TimecardView.propTypes = {
  currentShift: ImmutablePropTypes.map.isRequired,
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  fetchData: PropTypes.func.isRequired,
  fetchOvertime: PropTypes.func.isRequired,
  setCurrentShiftId: PropTypes.func.isRequired,
  hasTimecard: PropTypes.bool.isRequired,
  mandatedBreaks: ImmutablePropTypes.list.isRequired,
  locationRolesUpdated: PropTypes.number,
};

export default connect(
  (state, props) => ({
    breakPenaltiesEnabled: getBreakPenaltiesEnabled(state, props),
    currentShift: getCurrentShift(state, props),
    editMode: getEditMode(state, props),
    employeeOptions: getEmployeeOptions(state, props),
    expandBreaks: getExpandBreaks(state, props),
    focusClockOut: getFocusClockOut(state, props),
    hasTimecard: getHasTimecard(state, props),
    hideTipsSection: getHideTipsSection(state, props),
    isLoading: getIsLoading(state, props),
    isOpen: getIsOpen(state, props),
    isSubmitting: getIsSubmitting(state, props),
    locationName: getCurrentLocationName(state, props),
    lockTimesheetsPermission: getLockTimesheetRolePermission(state, props),
    mandatedBreaks: getMandatedBreaks(state, props),
    repackagingRolloutEnabled: getRepackagingRollout(state, props),
    roleOptions: getRoleOptions(state, props),
    shifts: getShifts(state, props),
    startingPath: getStartingPath(state, props),
    timesheetApprovalEnabled: getTimesheetApprovalEnabled(state, props),
    validationErrors: getValidationErrors(state, props),
    locationRolesUpdated: getLocationRolesUpdated(state),
    editableShift: getEditableShift(state),
    changesMade: getChangesMade(state),
  }),
  {
    addLocationRole: timesheetsActions.addLocationRole,
    closeModal: timecardModalActions.closeTimecardModal,
    dismissMissedBreaks,
    fetchData: fetchShiftData,
    fetchLocationData: fetchLocationDataSlice,
    fetchOvertime: fetchShiftOvertime,
    onDelete: deleteTimecard,
    setCurrentShiftId: timecardModalActions.setCurrentShiftId,
    setEditMode: timecardModalActions.setEditMode,
    setValidationErrors: timecardModalActions.setValidationErrors,
    submitTimecardChanges: submitTimecardData,
    onToggleMessengerShown: toggleMessengerShown,
    onShowExitWithoutSavingModal: showExitWithoutSavingModal,
    updateTimecardApproval,
    updateHasTimecards,
    setEditableShift: timecardModalActions.setEditableShift,
    showTimecardChangesSavedToast:
      timesheetsActions.showTimecardChangesSavedToast,
    setChangesMade: timecardModalActions.setChangesMade,
  }
)(TimecardView);
